import React, { useEffect, useState } from "react";
import { Flame, Menu, X, LogIn } from "lucide-react";
import { Link } from "react-router-dom";

const NAV_LINKS = [
  { label: "About", to: "/about" },
  { label: "Prayer", to: "/request-prayer" },
  { label: "Recordings", to: "/service-recordings" },
  { label: "Events", to: "/events" },
  { label: "Mentorship", to: "/prophetic-mentorship" },
  { label: "Give", to: "/give" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-[#C9A44C]/15"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="icon-badge !h-9 !w-9">
            <Flame className="h-4 w-4 text-[#F28C18] group-hover:text-[#E8452C] transition-colors" strokeWidth={1.5} />
            <span className="absolute inset-0 rounded-full" style={{ boxShadow: "0 0 18px rgba(242,140,24,0.45)" }} />
          </span>
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A44C]">RFMG</div>
            <div className="font-serif-display text-lg text-[#F6F0E6] -mt-0.5">Connect</div>
          </div>
        </Link>

        <ul className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-[#D9D9D9] hover:text-[#F6F0E6] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#C9A44C] hover:after:w-full after:transition-all"
              >
                {n.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/portal/login"
            className="inline-flex items-center gap-1.5 text-sm text-[#C9A44C] hover:text-[#E7C76F] transition-colors px-3 py-2 rounded-full border border-[#C9A44C]/30 hover:border-[#C9A44C]/60"
            data-testid="nav-member-login"
          >
            <LogIn className="h-3.5 w-3.5" />
            Member Login
          </Link>
          <Link to="/request-prayer" className="btn-ghost !py-2.5 !px-5 !text-sm" data-testid="nav-cta-prayer">
            Request Prayer
          </Link>
          <Link to="/give" className="btn-fire !py-2.5 !px-5 !text-sm" data-testid="nav-cta-give">
            Partner
          </Link>
        </div>

        <button
          className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#C9A44C]/30 text-[#F6F0E6]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-[#050505]/95 backdrop-blur-xl border-b border-[#C9A44C]/15 ${
          open ? "max-h-[600px]" : "max-h-0"
        }`}
      >
        <ul className="px-6 py-5 grid gap-1">
          {NAV_LINKS.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                onClick={() => setOpen(false)}
                className="block py-3 text-[#F6F0E6] border-b border-[#C9A44C]/10 last:border-0"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            </li>
          ))}
          <li className="pt-3">
            <Link to="/portal/login" onClick={() => setOpen(false)} className="btn-ghost w-full !py-3" data-testid="mobile-nav-member-login">
              <LogIn className="h-4 w-4" /> Member Login
            </Link>
          </li>
          <li>
            <Link to="/request-prayer" onClick={() => setOpen(false)} className="btn-fire w-full !py-3 mt-2">
              Request Prayer
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
};

export default Navbar;
